import {
  getErrorCode,
  getHostedOnboardingPrefillOptions,
  postHostedOnboardingPrefillChallengeOptions,
  postHostedOnboardingPrefillVerifiedOptions,
} from '@onefootprint/fetch-hosted';
import type {
  OnboardingRequirementCollectData,
  OnboardingSessionConfig,
  PostHostedOnboardingPrefillChallengeResponse,
  PrefillContext,
  VaultData,
  VerifiedPrefillResponse,
} from '@onefootprint/request-types';
import type { QueryClient } from '@tanstack/react-query';
import { retryOnConnectionError } from '@/idv2/components/errors/get-error-key';
import checkIsSocialMediaBrowser from '@/idv2/utils/check-is-social-media-browser';
import { checkDeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';

const { logInfo, logError, logWarn } = getLogger({ name: 'idv2', location: 'fetch-prefill-data' });

type RequestParams = {
  queryClient: QueryClient;
  headers: { 'X-Fp-Authorization': string };
  config?: OnboardingSessionConfig;
};

const canRunPasskeyChallenge = async (config?: OnboardingSessionConfig) => {
  if (checkIsSocialMediaBrowser(config)) {
    logInfo('Suppressing prefill passkey challenge - social media in-app browser');
    return false;
  }
  const { hasSupportForWebauthn } = await checkDeviceInfo();
  if (!hasSupportForWebauthn) {
    logInfo('Suppressing prefill passkey challenge - no webauthn support');
    return false;
  }
  return true;
};

type FootprintPrefillResult = {
  context: PrefillContext | undefined;
  challengeData: PostHostedOnboardingPrefillChallengeResponse | undefined;
};

async function fetchFootprintPrefill({ queryClient, headers, config }: RequestParams): Promise<FootprintPrefillResult> {
  try {
    const { context: rawContext } = await queryClient.fetchQuery(getHostedOnboardingPrefillOptions({ headers }));
    logInfo('Footprint prefill context fetched', { hasContext: !!rawContext });

    const canUsePasskey = !!rawContext?.supportsPasskeyChallenge && (await canRunPasskeyChallenge(config));

    // The API only returns a context when some challenge is runnable; keep that true after gating.
    if (!rawContext || (!rawContext.scrubbedEmailForChallenge && !canUsePasskey)) {
      return { context: undefined, challengeData: undefined };
    }

    const context = { ...rawContext, supportsPasskeyChallenge: canUsePasskey };

    // Pre-fetch email challenge if context has email
    if (!context.scrubbedEmailForChallenge) {
      return { context, challengeData: undefined };
    }

    const challengeData = await queryClient.fetchQuery({
      ...postHostedOnboardingPrefillChallengeOptions({
        headers,
        body: { challengeKind: 'email' },
      }),
      retry: retryOnConnectionError,
    });
    logInfo('Footprint prefill challenge fetched');

    return { context, challengeData };
  } catch (error) {
    logError('Failed to fetch Footprint prefill context', error);
    return { context: undefined, challengeData: undefined };
  }
}

type VerifiedPrefillResult = {
  response: VerifiedPrefillResponse | undefined;
  stagedData: VaultData | undefined;
};

async function fetchVerifiedPrefill({ queryClient, headers }: RequestParams): Promise<VerifiedPrefillResult> {
  try {
    const response = await queryClient.fetchQuery(postHostedOnboardingPrefillVerifiedOptions({ headers, body: {} }));
    logInfo('Verified prefill response fetched', { responseKind: response?.result?.kind });
    const stagedData = response?.result?.kind === 'located' ? response.result.stagedData : undefined;
    return { response, stagedData };
  } catch (error) {
    const RATE_LIMIT_EXCEEDED_ERROR_CODE = 'T123';
    if (getErrorCode(error) === RATE_LIMIT_EXCEEDED_ERROR_CODE) {
      logWarn('Rate limit exceeded for Verified prefill', error);
    } else {
      logError('Failed to fetch Verified prefill response', error);
    }
    return { response: undefined, stagedData: undefined };
  }
}

export type PrefillData = {
  allVaultData: VaultData;
  verifiedPrefillResponse: VerifiedPrefillResponse | undefined;
  footprintPrefillContext: PrefillContext | undefined;
  footprintPrefillChallengeData: PostHostedOnboardingPrefillChallengeResponse | undefined;
};

export async function fetchPrefillData({
  queryClient,
  token,
  currentRequirement,
  fullVault,
  config,
}: {
  queryClient: QueryClient;
  token: string;
  currentRequirement: OnboardingRequirementCollectData;
  fullVault: VaultData;
  config?: OnboardingSessionConfig;
}): Promise<PrefillData> {
  const headers = { 'X-Fp-Authorization': token };

  // Fetch Footprint prefill first (always check for returning users)
  const { context: footprintPrefillContext, challengeData: footprintPrefillChallengeData } =
    await fetchFootprintPrefill({ queryClient, headers, config });

  // Check if Footprint Prefill takes precedence
  const hasFootprintPrefill =
    !!footprintPrefillContext?.scrubbedEmailForChallenge || !!footprintPrefillContext?.supportsPasskeyChallenge;

  // Fetch Verified prefill only if enabled and Footprint Prefill doesn't take precedence
  const shouldFetchVerifiedPrefill = currentRequirement.isVerifiedPrefillEnabled && !hasFootprintPrefill;

  if (currentRequirement.isVerifiedPrefillEnabled && hasFootprintPrefill) {
    logInfo('Skipping Verified prefill - Footprint Prefill takes precedence');
  }

  const { response: verifiedPrefillResponse, stagedData } = shouldFetchVerifiedPrefill
    ? await fetchVerifiedPrefill({ queryClient, headers })
    : { response: undefined, stagedData: undefined };
  if (shouldFetchVerifiedPrefill) {
    logInfo('Verified prefill fetched', { responseKind: verifiedPrefillResponse?.result?.kind });
  }

  const allVaultData = stagedData ? { ...fullVault, ...stagedData } : fullVault;

  return {
    allVaultData,
    verifiedPrefillResponse,
    footprintPrefillContext,
    footprintPrefillChallengeData,
  };
}
